'use client'

import { useEffect, useId, useRef, useState } from 'react'
import { fareharborEmbedName, fareharborHeightFromMessage } from '@/lib/fareharbor'

/**
 * Inline FareHarbor calendar that auto-resizes to its content.
 *
 * FareHarbor only auto-resizes embeds its own script creates (it stamps
 * each with an embedId and resizes the matching element on postMessage). For
 * our hand-placed iframe we replicate that handshake: set the iframe `name`
 * so the FareHarbor app knows it's an inline embed and which embedId to tag
 * its height messages with, then listen for those messages and grow the
 * iframe. A min-height floor keeps it usable if no message ever arrives
 * (blocked postMessage, protocol drift, slow JS).
 *
 * The src is assigned in an effect AFTER the name, because the FareHarbor app
 * reads window.name at load time — setting the name after load is ignored.
 * This makes the inline calendar JS-dependent, which is inherent to any
 * third-party booking embed; the `button` layout is the no-JS fallback.
 */
export function FareharborInlineEmbed({
  url,
  minHeight = 640,
}: {
  url: string
  minHeight?: number
}) {
  const reactId = useId()
  const embedId = `fh${reactId.replace(/[^a-zA-Z0-9]/g, '')}`
  const ref = useRef<HTMLIFrameElement>(null)
  const [height, setHeight] = useState(minHeight)

  useEffect(() => {
    const iframe = ref.current
    if (!iframe) return
    // Name before src: the FareHarbor app reads window.name on load.
    iframe.name = fareharborEmbedName(embedId, window.location.href)
    iframe.src = url

    const onMessage = (event: MessageEvent) => {
      const next = fareharborHeightFromMessage({
        origin: event.origin,
        data: event.data,
        embedId,
      })
      if (next != null) setHeight((prev) => Math.max(prev, minHeight, Math.ceil(next)))
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [url, embedId, minHeight])

  return (
    <iframe
      ref={ref}
      title="Book with FareHarbor"
      loading="lazy"
      referrerPolicy="strict-origin-when-cross-origin"
      allow="payment"
      className="w-full block"
      style={{ height, border: '0' }}
    />
  )
}
